import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  MicrophoneIcon,
  StopCircleIcon,
  Cog6ToothIcon,
  SparklesIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
} from "@heroicons/react/24/solid";

import { getTokenOrRefresh } from "./token_util";


const sdk = require("microsoft-cognitiveservices-speech-sdk");

const tokenObj = await getTokenOrRefresh();

const audioConfig = sdk.AudioConfig.fromDefaultMicrophoneInput();

const speechConfig = sdk.SpeechConfig.fromAuthorizationToken(
  tokenObj.authToken,
  tokenObj.region
);

const conversationTranscriber = new sdk.ConversationTranscriber(
  speechConfig,
  audioConfig
);

const speechRecognizer = new sdk.SpeechRecognizer(speechConfig, audioConfig);

const speakerStyles = [
  {
    bubbleClass: "bg-white text-slate-800 ring-1 ring-slate-200",
    labelClass: "text-slate-500",
  },
  {
    bubbleClass: "bg-indigo-600 text-indigo-50",
    labelClass: "text-indigo-100",
  },
  {
    bubbleClass: "bg-emerald-600 text-emerald-50",
    labelClass: "text-emerald-100",
  },
  {
    bubbleClass: "bg-amber-500 text-slate-900",
    labelClass: "text-amber-900",
  },
  {
    bubbleClass: "bg-rose-600 text-rose-50",
    labelClass: "text-rose-100",
  },
  {
    bubbleClass: "bg-sky-600 text-sky-50",
    labelClass: "text-sky-100",
  },
];

export default function App() {
  const [displayText, setDisplayText] = useState(
    "INITIALIZED: ready to hear for you..."
  );
  const [recognizer, updateRecognizer] = useState(undefined);
  const [recognizedLines, setRecognizedLines] = useState([]);
  const [partialLine, setPartialLine] = useState("");
  const [conversationLines, setConversationLines] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [textScale, setTextScale] = useState(1);
  const [transcriptLayout, setTranscriptLayout] = useState("compact");

  const conversationTranscriptRef = useRef(null);
  const liveRecognitionRef = useRef(null);
  const appContainerRef = useRef(null);

  const isCompactLayout = transcriptLayout === "compact";
  const liveRecognitionHeight = isCompactLayout ? "10vh" : "10vh";
  const speechRecognizerHeight = isCompactLayout ? "45vh" : "45vh";

  const liveRecognitionText = useMemo(() => {
    const finalized = recognizedLines.join("\n");
    return partialLine
      ? `${finalized}${finalized ? "\n" : ""}${partialLine}`
      : finalized;
  }, [recognizedLines, partialLine]);

  const conversationMessages = useMemo(() => {
    const speakerMap = new Map();

    return conversationLines.map((entry, index) => {
      let rawSpeaker = "Unknown speaker";
      let messageText = "";

      if (entry && typeof entry === "object") {
        rawSpeaker = entry.speakerId || entry.speaker || rawSpeaker;
        messageText = entry.text || entry.content || messageText;
      } else if (typeof entry === "string") {
        const separatorIndex = entry.indexOf(":");
        if (separatorIndex > -1) {
          rawSpeaker = entry.slice(0, separatorIndex).trim() || rawSpeaker;
          messageText = entry.slice(separatorIndex + 1).trim();
        } else {
          messageText = entry;
        }
      }

      const speakerKey = rawSpeaker || "Unknown speaker";

      if (!speakerMap.has(speakerKey)) {
        const speakerNumber = speakerMap.size + 1;
        const isOddSpeaker = speakerNumber % 2 === 1;
        const styleIndex =
          (speakerNumber - 1) % (speakerStyles.length || 1);
        speakerMap.set(speakerKey, {
          alignment: isOddSpeaker ? "left" : "right",
          flexDirection: isOddSpeaker ? "row" : "row-reverse",
          label:
            rawSpeaker && rawSpeaker !== "Unknown speaker"
              ? rawSpeaker
              : `Speaker ${speakerNumber}`,
          styleIndex,
          containerPadding: isOddSpeaker ? "pr-8" : "pl-8",
        });
      }

      const { alignment, label, flexDirection, styleIndex, containerPadding } =
        speakerMap.get(speakerKey);

      return {
        key: `${speakerKey}-${index}`,
        label,
        text: messageText,
        alignment,
        flexDirection,
        styleIndex,
        containerPadding,
      };
    });
  }, [conversationLines]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const activeElement =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;
      setIsFullscreen(activeElement === appContainerRef.current);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange
      );
    };
  }, []);

  useLayoutEffect(() => {
    if (!autoScroll || !conversationTranscriptRef.current) {
      return;
    }

    conversationTranscriptRef.current.scrollTop =
      conversationTranscriptRef.current.scrollHeight;
  }, [conversationLines, autoScroll]);

  useLayoutEffect(() => {
    if (!autoScroll || !liveRecognitionRef.current) {
      return;
    }

    liveRecognitionRef.current.scrollTop =
      liveRecognitionRef.current.scrollHeight;
  }, [recognizedLines, partialLine, autoScroll]);

  async function toggleFullscreen() {
    const container = appContainerRef.current;
    if (!container) {
      return;
    }

    try {
      if (
        document.fullscreenElement === container ||
        document.webkitFullscreenElement === container ||
        document.mozFullScreenElement === container ||
        document.msFullscreenElement === container
      ) {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          await document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          await document.msExitFullscreen();
        }
      } else if (container.requestFullscreen) {
        await container.requestFullscreen();
      } else if (container.webkitRequestFullscreen) {
        await container.webkitRequestFullscreen();
      } else if (container.mozRequestFullScreen) {
        await container.mozRequestFullScreen();
      } else if (container.msRequestFullscreen) {
        await container.msRequestFullscreen();
      }
    } catch (error) {
      console.error("Fullscreen toggle failed", error);
    }
  }

  async function sttFromMicContinous() {
    if (recognizer) {
      setDisplayText("Status: Already listening...");
      return;
    }

    updateRecognizer(() => speechRecognizer);
    //setRecognizedLines([]);
    //setConversationLines([]);

    // Clean up event handlers if they exist
    speechRecognizer.recognizing = undefined;
    speechRecognizer.recognized = undefined;
    speechRecognizer.canceled = undefined;

    speechRecognizer.recognizing = (_s, e) => {
      setPartialLine(e.result.text);
    };

    speechRecognizer.recognized = (_s, e) => {
      if (e.result.reason === sdk.ResultReason.RecognizedSpeech) {
        // setRecognizedLines((prev) => [...prev, e.result.text]);
        setPartialLine(""); // Clear the partial line after finalizing
      }
    };

    speechRecognizer.canceled = () => {
      setDisplayText(
        "Canceled: Speech was cancelled or could not be recognized. Ensure your microphone is working properly."
      );
      updateRecognizer((current) => {
        if (current) {
          current.close();
        }
        return undefined;
      });
    };

    setDisplayText("Status: Listening...");
    speechRecognizer.startContinuousRecognitionAsync();
  }

  async function sttStopContinousRecognition() {
    updateRecognizer((current) => {
      if (current) {
        setDisplayText("Stopping Continuous Recognition...");
        current.stopContinuousRecognitionAsync(() => {
          current.close();
          setDisplayText("Status: Continuous recognition stopped.");
        });
      }
      setPartialLine("");
      return undefined;
    });
  }

  const hasActiveRecognizer = Boolean(recognizer);

  async function startDiarization() {
    conversationTranscriber.transcribing = undefined;
    conversationTranscriber.transcribed = undefined;
    conversationTranscriber.canceled = undefined;
    conversationTranscriber.sessionStarted = undefined;
    conversationTranscriber.sessionStopped = undefined;

    conversationTranscriber.sessionStarted = function () {};
    conversationTranscriber.sessionStopped = function () {
      conversationTranscriber.stopTranscribingAsync();
    };
    conversationTranscriber.canceled = function () {
      conversationTranscriber.stopTranscribingAsync();
    };
    conversationTranscriber.transcribed = function (_s, e) {
      const speakerId = e.result.speakerId || "Unknown speaker";
      const content = e.result.text || "";
      if (!content) {
        return;
      }
      setConversationLines((prev) => [
        ...prev,
        {
          speakerId,
          text: content,
        },
      ]);
    };

    conversationTranscriber.startTranscribingAsync(() => {});
  }

  async function stopDiarization() {
    conversationTranscriber.stopTranscribingAsync();
  }

  const transcriptFontStyles = useMemo(
    () => ({
      fontSize: `${textScale}rem`,
      lineHeight: `${1.6 * textScale}rem`,
    }),
    [textScale]
  );

  return (
    <div
      ref={appContainerRef}
      className="flex min-h-screen flex-col bg-slate-100 text-slate-900"
    >
      <header className="bg-indigo-600 text-indigo-50">
        <div className="mx-auto max-w-6xl px-6 pt-10 pb-24">
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-3xl bg-indigo-500/60">
                  <SparklesIcon className="h-6 w-6 text-white" />
                </span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.35em] text-indigo-100/80">
                    Live Captioning
                  </p>
                  <h1 className="text-3xl font-semibold text-white">
                    We Hear For You
                  </h1>
                </div>
              </div>

              <div className="flex flex-col gap-2 text-sm sm:items-end">
                <span className="inline-flex items-center gap-2 rounded-[9999px] bg-emerald-500/50 px-4 py-2 font-medium">
                  <span className="h-2 w-2 rounded-[9999px] bg-emerald-300" />
                  {displayText}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="-mt-16 flex min-h-0 flex-1 pb-16">
        <div className="mx-auto flex h-full min-h-0 w-full max-w-6xl flex-col px-6">
          <div className="flex h-full min-h-0 flex-col gap-6">
            <section
              className="flex flex-1 min-h-0 flex-col overflow-hidden rounded-3xl bg-white p-6 shadow-[0_25px_60px_-35px_rgba(79,70,229,0.45)]"
              style={{ height: "80vh" }}
            >
              <header className="flex flex-wrap justify-between gap-4 border-b border-slate-100 pb-4">
                <div className="flex flex-col items-between gap-2 text-sm font-medium">
                  <h2 className="text-2xl font-semibold text-slate-900">
                    Live transcription
                  </h2>
                  <p className="text-sm text-slate-400">
                    {autoScroll
                      ? "Auto-scrolling enabled."
                      : "Auto-scrolling paused."}
                  </p>
                </div>
                <div className=" nav flex items-center gap-3">
                  {partialLine && (
                    <span className="inline-flex items-center gap-2 rounded-[9999px] bg-emerald-50 px-4 py-1 text-xs font-semibold text-emerald-600">
                      <span className="h-2 w-2 animate-ping rounded-[9999px] bg-emerald-400" />
                      Speech Detected&hellip;
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      sttFromMicContinous();
                      startDiarization();
                    }}
                    disabled={hasActiveRecognizer}
                    className="inline-flex items-center gap-2 rounded-[9999px] bg-emerald-600 px-5 py-2 text-sm font-semibold  shadow-lg shadow-emerald-600/30 transition hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed disabled:bg-emerald-500/60 disabled:text-white/80"
                  >
                    <MicrophoneIcon className="h-5 w-5" />
                    Start
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      sttStopContinousRecognition();
                      stopDiarization();
                    }}
                    disabled={!hasActiveRecognizer}
                    className="inline-flex items-center gap-2 rounded-[9999px] bg-red-600 px-5 py-2 text-sm font-semibold shadow-lg shadow-red-600/30 transition hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed disabled:bg-red-500/60 disabled:text-white/80"
                  >
                    <StopCircleIcon className="h-5 w-5" />
                    Stop
                  </button>
                  <button
                    type="button"
                    onClick={toggleFullscreen}
                    className="inline-flex items-center gap-2 rounded-[9999px] bg-slate-900 px-5 py-2 text-sm font-semibold shadow-lg shadow-slate-900/30 transition hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-700 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
                  >
                    {isFullscreen ? (
                      <ArrowsPointingInIcon className="h-5 w-5" />
                    ) : (
                      <ArrowsPointingOutIcon className="h-5 w-5" />
                    )}
                    {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                  </button>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setSettingsOpen((prev) => !prev)}
                      className="inline-flex items-center gap-2 rounded-[9999px] bg-slate-900 px-5 py-2 text-sm font-semibold shadow-lg shadow-slate-900/30 transition hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-700 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
                    >
                      <Cog6ToothIcon className="h-5 w-5" />
                      Settings
                    </button>
                    {settingsOpen && (
                      <div className="absolute right-0 top-12 z-20 w-64 rounded-2xl bg-white p-4 text-sm text-slate-700 shadow-xl">
                        <p className="text-xs font-semibold uppercase tracking-widest text-indigo-600">
                          Session preferences
                        </p>
                        <label className="mt-3 flex items-center justify-between gap-3 text-xs">
                          Auto scroll transcript
                          <input
                            type="checkbox"
                            checked={autoScroll}
                            onChange={(event) =>
                              setAutoScroll(event.target.checked)
                            }
                            className="h-4 w-4 rounded border-indigo-200 text-indigo-600 focus:ring-indigo-500"
                          />
                        </label>
                        <label className="mt-4 flex flex-col gap-2 text-xs">
                          <span className="flex items-center justify-between">
                            Text size
                            <span className="font-semibold text-indigo-600">
                              {Math.round(textScale * 100)}%
                            </span>
                          </span>
                          <input
                            type="range"
                            min="0.8"
                            max="1.6"
                            step="0.05"
                            value={textScale}
                            onChange={(event) =>
                              setTextScale(Number(event.target.value))
                            }
                            className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-indigo-500"
                          />
                        </label>
                        <div className="mt-4 flex flex-col gap-2 text-xs">
                          <span className="flex items-center justify-between">
                            Layout
                            <span className="font-semibold text-indigo-600 capitalize">
                              {transcriptLayout}
                            </span>
                          </span>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => setTranscriptLayout("compact")}
                              className={`flex-1 rounded-[9999px] border px-3 py-2 font-semibold transition ${
                                isCompactLayout
                                  ? "border-indigo-500 bg-indigo-50 text-indigo-600"
                                  : "border-slate-200 text-slate-500 hover:border-indigo-200 hover:text-indigo-600"
                              }`}
                            >
                              Compact
                            </button>
                            <button
                              type="button"
                              onClick={() => setTranscriptLayout("full")}
                              className={`flex-1 rounded-[9999px] border px-3 py-2 font-semibold transition ${
                                !isCompactLayout
                                  ? "border-indigo-500 bg-indigo-50 text-indigo-600"
                                  : "border-slate-200 text-slate-500 hover:border-indigo-200 hover:text-indigo-600"
                              }`}
                            >
                              Full
                            </button>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setRecognizedLines([]);
                            setConversationLines([]);
                            setPartialLine("");
                            setDisplayText("Transcript cleared.");
                            setSettingsOpen(false);
                          }}
                          className="mt-4 w-full rounded-[9999px] bg-indigo-50 px-3 py-2 text-xs font-semibold text-indigo-600 transition hover:bg-indigo-100"
                        >
                          Clear transcript
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </header>

              <div className="mt-4 flex flex-1 min-h-0 flex-col gap-8">
                <div
                  ref={conversationTranscriptRef}
                  className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden rounded-2xl bg-slate-50 p-6 text-sm leading-6 text-slate-700"
                  style={{
                    ...transcriptFontStyles,
                    height: speechRecognizerHeight,
                    minHeight: speechRecognizerHeight,
                    maxHeight: speechRecognizerHeight,
                  }}
                >
                  {conversationMessages.length === 0 ? (
                    <p className="text-sm text-slate-400">
                      Conversation history will appear here once diarization starts.
                    </p>
                  ) : (
                    <div className="flex flex-col gap-3">
                      {conversationMessages.map((message) => (
                        <div
                          key={message.key}
                          className={`flex w-full justify-start ${
                            message.flexDirection === "row-reverse"
                              ? "flex-row-reverse"
                              : "flex-row"
                          } ${message.containerPadding}`}
                        >
                          {(() => {
                            const style =
                              speakerStyles[message.styleIndex] ||
                              speakerStyles[0];
                            const bubbleCorner =
                              message.alignment === "right"
                                ? "rounded-br-md"
                                : "rounded-bl-md";

                            return (
                              <div
                                className={`max-w-[75%] rounded-2xl ${bubbleCorner} px-4 py-3 shadow-sm shadow-slate-900/5 ${style.bubbleClass}`}
                              >
                                <span
                                  className={`text-xs font-semibold uppercase tracking-wide ${style.labelClass}`}
                                >
                                  {message.label}
                                </span>
                                <p className="mt-1 whitespace-pre-wrap break-words">
                                  {message.text}
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div
                  ref={liveRecognitionRef}
                  className="mt-4 flex-none overflow-y-auto overflow-x-hidden rounded-2xl bg-slate-50 p-6 text-sm leading-6 text-slate-700 whitespace-normal break-words"
                  style={{
                    ...transcriptFontStyles,
                    height: liveRecognitionHeight,
                    minHeight: liveRecognitionHeight,
                    maxHeight: liveRecognitionHeight,
                  }}
                >
                  {liveRecognitionText ||
                    "Live recognition will appear here once you start listening."}
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}